/* Kozie — related-products rail navigation.

   The rail is a native horizontal scroller with CSS scroll-snap. This file
   does NOT move it by hand: it reports position and drives the buttons, and
   every actual movement goes through scrollTo, so momentum, rubber-banding,
   keyboard scrolling and screen-reader behaviour all remain the browser's.

   It attaches on connect and again whenever the recommendations markup is
   replaced — Dawn fetches this section over the network after the page loads,
   so the elements this script needs do not exist at first paint. */
(() => {
  const SEL = '.kozie-related__rail';

  function setup(rail) {
    if (!rail || rail.dataset.kozieBound === 'true') return;
    /* The reserved-height placeholder is built from the same markup as the
       real rail — same class, same number of <li> — so it binds like one if
       nothing stops it. Binding it sets data-kozie-bound, which switches OFF
       the `:not([data-kozie-bound]) .kozie-related__item { opacity: 1 }` guard
       in the stylesheet, and sync() then marks card 0 active and leaves the
       other three at 45%. Three of four grey rectangles fading out is the
       opposite of the steady block the placeholder exists to be. It also
       attaches a scroll listener and a ResizeObserver to a subtree that is
       about to be thrown away. */
    if (rail.closest('[data-kozie-placeholder]')) return;
    const root = rail.closest('.kozie-related');
    if (!root) return;
    const items = [...rail.querySelectorAll('.kozie-related__item')];
    const dots = [...root.querySelectorAll('.kozie-related__dot')];
    const prev = root.querySelector('[data-kozie-prev]');
    const next = root.querySelector('[data-kozie-next]');
    const nav = root.querySelector('.kozie-related__nav');
    if (!items.length) return;
    rail.dataset.kozieBound = 'true';

    /* The quick-add pop-ups cannot stay in the rail.
       .kozie-related__item sits at opacity 0.45 unless it is the active card,
       and opacity applies to EVERY descendant — including a position: fixed
       one — so a pop-up opened from a recessed card would render at 45%, and
       an ancestor's opacity cannot be undone from inside it. Moving each
       <quick-add-modal> to <body> is the fix.
       Safe to move: ModalDialog wires its listeners in the constructor, so a
       reconnect costs nothing, and the id the opener looks up travels with it.
       Everything this rail moved on a previous pass is cleared FIRST, and by a
       marker rather than by id. Deduplicating by id was not enough, and the
       product page proved it: the section renders a fallback rail at first
       paint, scan() binds it and moves its four pop-ups to <body>, and then
       fill() replaces the rail with the real recommendations — a DIFFERENT
       four products, so different ids, so the first four matched nothing and
       stayed in <body> for the life of the page. Measured on the preview:
       eight pop-ups in <body> for four cards, four of them orphaned with no
       opener left to reach them. A marker catches them whatever the ids are.

       Only ever this rail's own: the marker is set here, so a pop-up belonging
       to anything else — complementary products, a collection card — is never
       touched. */
    /* Tagged with THIS rail's own key, not a bare flag. There can be more than
       one rail on a page now that the home page uses the same component, and a
       bare flag would have made the second rail to bind wipe the first one's
       pop-ups out of <body> on its way past. */
    if (!rail.dataset.kozieRailKey) {
      setup.railSeq = (setup.railSeq || 0) + 1;
      rail.dataset.kozieRailKey = 'r' + setup.railSeq;
    }
    const railKey = rail.dataset.kozieRailKey;

    document
      .querySelectorAll('body > quick-add-modal[data-kozie-rail="' + railKey + '"]')
      .forEach((stale) => stale.remove());

    rail.querySelectorAll('quick-add-modal').forEach((modal) => {
      modal.dataset.kozieRail = railKey;

      /* Do Dawn's own bookkeeping BEFORE moving, then close its guard.

         ModalDialog.connectedCallback is:

           this.moved || (this.moved = true,
             this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-',''),
             document.body.appendChild(this))

         — no null guard on that closest(). quick-add.js is deferred, so on the
         product page the element is still un-upgraded when this runs: we move it
         to <body>, quick-add.js defines the class a moment later, the upgrade
         fires connectedCallback, closest() is now null and it throws
         "Cannot read properties of null (reading 'id')". Measured on the live
         preview: four uncaught TypeErrors per product page, one per rail card,
         and dataset.section left unset on every one of them.

         It was not fatal — show() fetches the plain product URL — but it is four
         uncaught exceptions in every error monitor, and anything Dawn adds after
         that line in connectedCallback would silently never run.

         So: read the section while the modal is still inside it, set the same
         dataset Dawn would have set, then set `moved` so Dawn's callback
         short-circuits on its own guard instead of dereferencing null. The end
         state is byte-identical to what Dawn would have produced. */
      const section = modal.closest('[id^="shopify-section-"]');
      if (section && !modal.dataset.section) {
        modal.dataset.section = section.id.replace('shopify-section-', '');
      }
      modal.moved = true;

      document.body.appendChild(modal);
    });

    const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Position is measured GEOMETRICALLY — the item whose centre sits nearest
       the rail's centre — not arithmetically from scrollLeft divided by a card
       width.

       The first version did the arithmetic, and it broke at the end of the
       rail: once the scroller hits its maximum the last card stops moving, so
       scrollLeft never reaches the value that formula expected and the last
       product could not be selected. Measuring where the cards actually are
       has no such edge, and it works unchanged whether the CSS is snapping to
       centre (phone) or to start (desktop). */
    function nearest() {
      const mid = rail.scrollLeft + rail.clientWidth / 2;
      let best = 0;
      let bestGap = Infinity;
      items.forEach((el, n) => {
        const c = el.offsetLeft + el.offsetWidth / 2;
        const d = Math.abs(c - mid);
        if (d < bestGap) { bestGap = d; best = n; }
      });
      return best;
    }

    /* True once the rail is scrolled as far as it goes, within a pixel. The
       last card can be the nearest to centre well before this on desktop, so
       "am I at the end" and "is the last card active" are different questions
       and the buttons need the first one. */
    const atStart = () => rail.scrollLeft <= 1;
    const atEnd = () => rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1;

    /* ── paging ──────────────────────────────────────────────────────────
       The dots used to be one per CARD, and with more than one card on screen
       that is the wrong count. Five cards shown two-up have four reachable
       start positions, not five: the fifth card can never be the leading one,
       so the fifth dot could never light. Scrolled to the very end the
       highlight sat on the fourth of five, reading as "there is still one more
       to go" when there was not.

       Both numbers are measured rather than counted, so they follow the card
       width, the gap and the viewport with nothing to keep in sync: a page is
       one card plus one gap (taken from the distance between the first two
       cards), and the number of pages is how many of those fit in the
       scrollable distance, plus the first. Recomputed on every sync, so a
       rotate or a resize re-pages the rail by itself.

       Card highlighting is left alone: nearest() still drives `is-active`,
       because the fade on the product page marks the card being READ, which is
       a different question from which page the rail is on. */
    function step() {
      const a = items[0];
      const b = items[1];
      if (a && b) return Math.abs(b.offsetLeft - a.offsetLeft);
      return a ? a.offsetWidth : rail.clientWidth;
    }

    function maxScroll() {
      return Math.max(0, rail.scrollWidth - rail.clientWidth);
    }

    /* CEIL, not round. The scrollable distance is almost never a whole number
       of steps: four cards at 271px each in a 440px rail leave 665px of travel,
       which is 2.45 steps. Rounding that DOWN said three pages, so the last
       page sat at 2 x 271 = 542 while the rail actually goes to 665 — the
       fourth card stayed 123px off the right edge, clipped and faded, and the
       next arrow was live (atEnd() is false at 542) but paged to a number
       goToPage immediately clamped back, so it moved nothing at all. Ceiling it
       adds the short final page, and goToPage already clamps that page's target
       to maxScroll, so the last page lands exactly at the end with the last
       card whole. The 1px slack stops a sub-pixel overhang from inventing a
       page nobody can tell apart from the one before it. */
    function pageCount() {
      const s = step();
      if (s <= 0) return 1;
      return Math.max(1, Math.min(items.length, Math.ceil(Math.max(0, maxScroll() - 1) / s) + 1));
    }

    /* The last page is a SHORT one — 665 is 2.45 steps, not 3 — so dividing
       scrollLeft by the step under-counts at the end and would leave the
       highlight on the second-to-last dot while the rail is hard against its
       stop. Scrolled all the way is the last page by definition, whatever the
       arithmetic says; everywhere else the steps are whole and the division is
       right. */
    function pageNow() {
      const s = step();
      if (s <= 0) return 0;
      if (atEnd()) return pageCount() - 1;
      return Math.min(pageCount() - 1, Math.max(0, Math.round(rail.scrollLeft / s)));
    }

    function goToPage(p) {
      const target = Math.min(maxScroll(), Math.max(0, Math.min(pageCount() - 1, p)) * step());
      scrollRailTo(target);
    }

    function goTo(i) {
      const el = items[Math.min(Math.max(i, 0), items.length - 1)];
      if (!el) return;
      /* Scroll so this card's centre meets the rail's centre, then let the
         browser clamp it at the ends. Used by tap-to-select, which is about
         bringing ONE named card forward rather than turning a page. */
      scrollRailTo(el.offsetLeft + el.offsetWidth / 2 - rail.clientWidth / 2);
    }

    function scrollRailTo(target) {
      const from = rail.scrollLeft;
      rail.scrollTo({ left: target, behavior: reduce() ? 'auto' : 'smooth' });

      /* Smooth scrolling is not guaranteed. Where it is unavailable — a
         browser flag, an enterprise policy, a platform that ignores it — this
         call moves nothing at all rather than jumping, and on desktop these
         arrows and dots are the ONLY way to move the rail: a mouse cannot swipe
         a scroll-snap strip. A control that silently does nothing is worse than
         one that jumps.

         So: check afterwards, and only if the scroller has not moved AT ALL
         from where it started, redo the scroll instantly. Guarded on `===`
         rather than a tolerance, so a scroll that merely landed short — or one
         the shopper has taken over with a finger — is left alone. */
      if (!reduce()) {
        setTimeout(() => {
          if (rail.scrollLeft === from) rail.scrollTo({ left: target, behavior: 'auto' });
        }, 260);
      }
    }

    function sync() {
      const i = nearest();
      items.forEach((el, n) => el.classList.toggle('is-active', n === i));
      if (prev) prev.disabled = atStart();
      if (next) next.disabled = atEnd();
      /* Nothing to page to means no controls at all, rather than a row of
         controls that do nothing.

         This used to hide only the dots, which left the arrows behind — and
         when the rail does not overflow, atStart() and atEnd() are BOTH true,
         so both arrows were disabled. On desktop that is the normal case, not
         an edge case: four cards at 20.6rem plus gaps and edges measure about
         934px, so any viewport wider than that fits the whole rail. Every
         desktop shopper was getting two greyed-out chevrons with an empty gap
         between them under the section. The nav row goes as a whole now. */
      const paged = rail.scrollWidth > rail.clientWidth + 1;

      /* Liquid prints one dot per product, because it cannot know how many
         cards will fit. The surplus are hidden here rather than left inert:
         a dot that can never be reached is worse than no dot. */
      const pages = pageCount();
      const here = pageNow();
      dots.forEach((d, n) => {
        d.hidden = !paged || n >= pages;
        d.setAttribute('aria-current', n === here ? 'true' : 'false');
      });
      if (nav) nav.hidden = !paged;
    }

    /* A tap on a receded card brings it forward instead of leaving the page.

       Only where the cards are actually receded. The stylesheet publishes
       --kzr-tap-select for exactly this: 1 on a phone where one card is read
       at a time, 0 on desktop where all four are legible and a tap should go
       straight to the product. Reading the flag from CSS keeps the breakpoint
       in one file instead of duplicating a media query here, where it would
       quietly fall out of step the first time the layout changed. */
    const tapSelects = () =>
      getComputedStyle(rail).getPropertyValue('--kzr-tap-select').trim() === '1';

    const indexOfCard = (node) => {
      const li = node.closest('.kozie-related__item');
      return li ? items.indexOf(li) : -1;
    };

    rail.addEventListener('click', (e) => {
      if (!tapSelects()) return;
      const i = indexOfCard(e.target);
      /* nearest() is recomputed rather than reading .is-active, because that
         class is set on a rAF after the scroll settles and would be stale for
         a tap that lands mid-glide. */
      if (i < 0 || i === nearest()) return;
      e.preventDefault();
      goTo(i);
    });

    /* Keyboard users get the same result without being trapped.

       Blocking Enter on a receded card would strand someone tabbing through
       the rail: they cannot see a fade the way a pointer user does, and the
       link would simply stop working. Instead, focus brings the card forward,
       so by the time Enter is pressed it is the active one and the click
       handler above lets it through. */
    rail.addEventListener('focusin', (e) => {
      if (!tapSelects()) return;
      const i = indexOfCard(e.target);
      if (i >= 0 && i !== nearest()) goTo(i);
    });

    prev && prev.addEventListener('click', () => goToPage(pageNow() - 1));
    next && next.addEventListener('click', () => goToPage(pageNow() + 1));
    dots.forEach((d, n) => d.addEventListener('click', () => goToPage(n)));

    let raf;
    rail.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(sync);
      },
      { passive: true }
    );

    if (window.ResizeObserver) new ResizeObserver(sync).observe(rail);
    sync();
  }

  /* Clear pop-ups in <body> that nothing can reach any more.

     The marker sweep in setup() removes what THIS rail moved on a previous
     pass, and its comment claims that catches the fallback rail's four
     "whatever the ids are". Measured on the live product page, it does not:

       8 <quick-add-modal> in <body> for 4 cards
       4 tagged data-kozie-rail="r1"  — the fallback rail's, ours
       4 tagged with nothing at all   — the real rail's, moved by DAWN
       3 orphaned, with no opener anywhere on the page
       1 id duplicated three times over (the modal, its close button, its info)

     The marker cannot catch them because of an ordering the original note did
     not anticipate. fill() replaces the whole <ul>, so the new rail is a new
     element with no rail key and gets a fresh one — r2. By the time setup()
     runs on it, quick-add.js has already upgraded its modals and Dawn's own
     connectedCallback has appended them to <body>. So rail.querySelectorAll
     finds nothing left to tag, r2 tags nobody, and r2's sweep removes only
     r2's own — which is an empty set. The r1 four stay for the life of the page.

     Identity is the wrong test here; REACHABILITY is the right one. A pop-up
     that no modal-opener refers to cannot be opened by anyone, whoever moved it
     and whatever it is tagged with. This is the same sweep kozie-collection.js
     already runs on collection pages, where filtering creates the identical
     mess; the product page simply never had it.

     Kept: anything currently open (removing it would close a dialog under the
     shopper), and the LAST copy of each id — the freshest, since Dawn appends.

     Guarded on there being at least one opener. Mid-replacement the rail is
     briefly empty, and sweeping then would delete perfectly good pop-ups that
     the arriving cards are about to point at. */
  function sweepBodyModals() {
    const openers = document.querySelectorAll('modal-opener[data-modal]');
    if (!openers.length) return;

    const wanted = {};
    openers.forEach((o) => {
      const v = o.getAttribute('data-modal');
      if (v && v.charAt(0) === '#') wanted[v.slice(1)] = true;
    });

    /* getAttribute, never .id — .id on an element containing an <input name="id">
       returns the INPUT, not the string, and these pop-ups contain exactly that. */
    const modals = document.querySelectorAll('body > quick-add-modal[id]');
    const seen = {};
    for (let i = modals.length - 1; i >= 0; i--) {
      const m = modals[i];
      const id = m.getAttribute('id');
      if (m.hasAttribute('open')) { seen[id] = true; continue; }
      if (seen[id]) { m.remove(); continue; }
      if (!wanted[id]) { m.remove(); continue; }
      seen[id] = true;
    }
  }

  function scan() {
    document.querySelectorAll(SEL).forEach(setup);
    /* After setup, so this rail's own pop-ups are in <body> and its openers
       exist before anything is judged unreachable. scan() is what the
       MutationObserver calls, so a rail Dawn replaces is swept on replacement
       rather than only at first paint. */
    sweepBodyModals();
  }

  /* Load the recommendations earlier than Dawn would.

     Dawn observes this section with rootMargin 400px — about half a phone
     screen of warning, which is less time than the request takes, so the cards
     land after the section is already in view. Rather than edit global.js,
     which is a core theme file that a Dawn upgrade would overwrite, this
     watches with a much larger margin and calls the element's own public
     loadRecommendations(). Dawn's observer is then disconnected so the same
     request is not made twice. */
  function preload() {
    /* Scoped to .kozie-related on purpose. <product-recommendations> is not a
       unique tag: main-product.liquid renders a second one for the
       "Complementary products" block. A bare tag lookup returns whichever
       comes first in the document — the complementary block — so the sage rail
       would never be preloaded and the MutationObserver below would watch the
       wrong subtree. Nothing enables that block today; this makes turning it
       on in the editor a non-event instead of a silent breakage. */
    const el = document.querySelector('product-recommendations.kozie-related[data-url]');
    if (!el || !el.dataset.productId || !el.dataset.sectionId) return;

    /* Claimed BEFORE the observer is constructed, not inside its callback.
       This function is called twice — once immediately (the script is defer,
       so it runs before DOMContentLoaded) and once from the DOMContentLoaded
       listener below, which covers the case where the script is re-executed
       after that event has already fired. Setting the flag in the callback
       meant both calls built an observer, both fired, and the recommendations
       were fetched twice. Both call sites are kept; only one observer is. */
    if (el.dataset.koziePreloaded === 'true') return;
    el.dataset.koziePreloaded = 'true';

    new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        try { el.observer?.unobserve(el); } catch (e) { /* Dawn's field may move */ }
        fill(el);
      },
      { rootMargin: '0px 0px 1400px 0px' }
    ).observe(el);
  }

  /* Fills the reserved-height placeholder — and clears it away when there is
     nothing to fill it with.

     This used to call Dawn's el.loadRecommendations() and then start an
     eight-second timer that removed the section if no real card had appeared.
     That timer could not tell "the response was empty" from "the response is
     still in flight", because Dawn's method does not return its promise. On a
     slow connection it destroyed the element that the in-flight response was
     about to write into, and the shopper was left with nothing at all — while
     on the roughly one product in five whose recommendations come back empty,
     the skeleton sat there for the full eight seconds before collapsing.

     Doing the fetch here instead of guessing at it settles both: the empty
     case is known the moment the response lands (and the section goes before
     the shopper has scrolled to it, since this runs 1400px early), and the
     slow case can no longer be mistaken for the empty one. The response
     handling is the same as Dawn's — same URL, same "only replace if the
     response actually has content" test. */
  function fill(el) {
    const url =
      el.dataset.url + '&product_id=' + el.dataset.productId + '&section_id=' + el.dataset.sectionId;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const holder = document.createElement('div');
        holder.innerHTML = text;
        const fresh = holder.querySelector('product-recommendations');
        const markup = fresh ? fresh.innerHTML.trim() : '';

        if (markup.length) {
          el.innerHTML = markup;
          return;
        }

        /* Empty response. If the section rendered a fallback rail on first
           paint there is already something worth keeping here, so leave it
           alone — that is the whole point of the fallback. Only a section
           still showing the skeleton placeholder has nothing to show, and
           only that one gets removed. */
        if (el.querySelector('[data-kozie-placeholder]')) drop(el);
      })
      .catch(() => {
        if (el.querySelector('[data-kozie-placeholder]')) drop(el);
      });
  }

  /* Takes the whole section, not just <product-recommendations>: the element
     sits inside a colour-scheme wrapper and a .shopify-section, and removing
     only the inner element left an empty coloured strip behind.

     Never in the theme editor. A merchant configuring this section has to be
     able to see it, and a product with no recommendations would otherwise make
     the section vanish from under them mid-edit. */
  function drop(el) {
    if (window.Shopify && window.Shopify.designMode) return;
    (el.closest('.shopify-section') || el).remove();
  }

  document.addEventListener('DOMContentLoaded', scan);
  scan();
  preload();
  document.addEventListener('DOMContentLoaded', preload);

  /* Dawn swaps the whole <product-recommendations> subtree in once its fetch
     resolves, which throws away anything bound before that and is why a plain
     DOMContentLoaded hook is not enough on its own.

     A function, not a one-off, because the theme editor replaces the host
     element itself — see the section:load handler below. An observer created
     against the old host would keep watching a detached node while the live
     one went unwatched, and since fill() rewrites innerHTML AFTER scan() has
     run, nothing would ever bind the replacement rail. The dataset flag makes
     re-running this free. */
  function watch() {
    const host = document.querySelector('product-recommendations.kozie-related');
    if (!host || !window.MutationObserver) return;
    if (host.dataset.kozieWatched === 'true') return;
    host.dataset.kozieWatched = 'true';
    new MutationObserver(scan).observe(host, { childList: true, subtree: true });
  }
  watch();

  /* Theme editor. Shopify replaces the whole section on every settings change,
     which throws away the element these observers were pointed at. The browser
     will not re-execute a <script src> it already has cached, so without this
     the merchant gets a rail with dead arrows and dots, and an empty one that
     never fetches, for the rest of the editing session. scan() is idempotent
     (data-kozie-bound) and preload() claims its own flag, so re-running both is
     safe; the fresh element carries neither, which is exactly what makes it
     re-bind. Storefront visitors never fire this event. */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && !event.target.querySelector('.kozie-related__rail')) return;
    watch();
    scan();
    preload();
  });
})();
